// ── Throttle display helpers ────────────────────────────────────────────────
// Pure functions shared by the Chunk Throttle overview and server detail views.

import { formatDistanceToNow } from 'date-fns';
import type {
  ChunkSample,
  ChunkThrottleConfig,
  MsptThrottleConfig,
  PlayerCountThrottleConfig,
  ServerWideThrottle,
} from '../types/throttle';

/** The server tick budget in ms — sustained MSPT above this means < 20 TPS. */
export const TICK_BUDGET_MS = 50;

export type MsptSeverity = 'ok' | 'warn' | 'critical';

/**
 * Severity bands against the 50 ms tick budget:
 * comfortable well under 50 ms, warning approaching it, critical above it.
 */
export const getMsptSeverity = (mspt: number): MsptSeverity => {
  if (mspt > TICK_BUDGET_MS) return 'critical';
  if (mspt >= TICK_BUDGET_MS * 0.8) return 'warn';
  return 'ok';
};

/** Text color for inline MSPT values (theme-safe badge palette). */
export const MSPT_TEXT_CLASSES: Record<MsptSeverity, string> = {
  ok: 'text-green-300',
  warn: 'text-yellow-300',
  critical: 'text-red-300',
};

/** Badge classes for MSPT pills, matching the documented status-badge pattern. */
export const MSPT_BADGE_CLASSES: Record<MsptSeverity, string> = {
  ok: 'bg-green-500/20 text-green-300 border-green-500/30',
  warn: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  critical: 'bg-red-500/20 text-red-300 border-red-500/30',
};

/**
 * Severity color for a chunk's MSPT relative to the effective flagging
 * threshold (`null` threshold = relative rule / unknown → generic warning).
 */
export const chunkMsptThresholdClass = (chunkMspt: number, threshold: number | null): string => {
  if (threshold != null && threshold > 0) {
    if (chunkMspt >= threshold * 2) return 'text-red-300';
    if (chunkMspt >= threshold) return 'text-yellow-300';
    return 'text-glass-300';
  }
  return 'text-yellow-300';
};

/** Gauge color for a TPS dial (same palette as the Status Monitor cards). */
export const getTpsGaugeColor = (tps: number): string => {
  if (tps >= 18) return '#60A5FA'; // blue-400
  if (tps >= 15) return '#A78BFA'; // purple-400
  if (tps >= 10) return '#F472B6'; // pink-400
  return '#EF4444'; // red-500
};

/**
 * Fills the optional `ChunkSample` fields with the handoff-recommended `??`
 * defaults so render code doesn't have to null-check every access.
 */
export interface NormalizedChunkSample {
  dimension: string;
  posX: number;
  posZ: number;
  mspt: number;
}

export const normalizeChunkSample = (chunk: ChunkSample): NormalizedChunkSample => ({
  dimension: chunk.dimension ?? 'minecraft:overworld',
  posX: chunk.posX ?? 0,
  posZ: chunk.posZ ?? 0,
  mspt: chunk.mspt ?? 0,
});

/** Block coordinates of a chunk's center (for map links / teleport commands). */
export const chunkCenterBlock = (x: number, z: number): { x: number; z: number } => ({
  x: x * 16 + 8,
  z: z * 16 + 8,
});

/** Block coordinates of a chunk's corner. */
export const chunkCornerBlock = (x: number, z: number): { x: number; z: number } => ({
  x: x * 16,
  z: z * 16,
});

/** Copy-pasteable teleport command targeting the chunk's center. */
export const buildTeleportCommand = (dimension: string, chunkX: number, chunkZ: number): string => {
  const center = chunkCenterBlock(chunkX, chunkZ);
  return `/execute in ${dimension} run tp @s ${center.x} ~ ${center.z}`;
};

/** Strips the `minecraft:` prefix for compact display ("overworld", "the_nether"). */
export const formatDimension = (dimension: string): string =>
  dimension.startsWith('minecraft:') ? dimension.slice('minecraft:'.length) : dimension;

const TARGET_ALL = 'target:all';
const TARGET_CLUSTER_PREFIX = 'target:cluster:';
const TARGET_SINGLE_PREFIX = 'target:single:';

/** Human-readable label for a config `target` value. */
export const describeThrottleTarget = (target: string): string => {
  if (target === TARGET_ALL) return 'All servers';
  if (target.startsWith(TARGET_CLUSTER_PREFIX)) {
    return `Cluster ${target.slice(TARGET_CLUSTER_PREFIX.length)}`;
  }
  if (target.startsWith(TARGET_SINGLE_PREFIX)) {
    return `Server ${target.slice(TARGET_SINGLE_PREFIX.length)}`;
  }
  return target;
};

/**
 * A per-chunk MSPT tier. Entries written before the API tagged rule types
 * carry no `type` and are MSPT tiers; anything tagged with a type this build
 * does not know is deliberately claimed by neither guard, so new rule kinds
 * are skipped rather than rendered with missing fields.
 */
export const isMsptThrottleConfig = (rule: ChunkThrottleConfig): rule is MsptThrottleConfig =>
  rule.type == null || rule.type === 'MSPT';

/** A server-wide population throttle (`type: "PC"`). */
export const isPlayerCountThrottleConfig = (
  rule: ChunkThrottleConfig,
): rule is PlayerCountThrottleConfig => rule.type === 'PC';

/** Rules this build cannot render — a rule type added after it shipped. */
export const isUnknownThrottleConfig = (rule: ChunkThrottleConfig): boolean =>
  !isMsptThrottleConfig(rule) && !isPlayerCountThrottleConfig(rule);

/**
 * Keeps the rules that apply to a server: every rule at the most specific
 * matching target level (`single` > `cluster` > `all`). Cluster ids are
 * case-insensitive, matching the API's routing.
 *
 * Precedence is resolved inside the list it is handed, so callers pass one
 * rule family at a time — a server-specific chunk tier must not hide the
 * network-wide population throttles.
 */
const resolveEffectiveRules = <T extends { target: string }>(
  rules: T[],
  fqdn: string | null | undefined,
  clusterId: string | null | undefined,
): T[] => {
  const fqdnLower = fqdn?.toLowerCase();
  const clusterLower = clusterId?.toLowerCase();

  const singles: T[] = [];
  const clusters: T[] = [];
  const alls: T[] = [];

  for (const rule of rules) {
    const target = rule.target.toLowerCase();
    if (fqdnLower && target === `${TARGET_SINGLE_PREFIX}${fqdnLower}`) {
      singles.push(rule);
    } else if (clusterLower && target === `${TARGET_CLUSTER_PREFIX}${clusterLower}`) {
      clusters.push(rule);
    } else if (target === TARGET_ALL) {
      alls.push(rule);
    }
  }

  return singles.length > 0 ? singles : clusters.length > 0 ? clusters : alls;
};

/**
 * The per-chunk MSPT tiers in force on a server, sorted by ascending
 * threshold. Returns `[]` when nothing matches (built-in defaults).
 */
export const resolveEffectiveThrottleConfigs = (
  configs: ChunkThrottleConfig[],
  fqdn: string | null | undefined,
  clusterId: string | null | undefined,
): MsptThrottleConfig[] =>
  resolveEffectiveRules(configs.filter(isMsptThrottleConfig), fqdn, clusterId).sort(
    (a, b) => a.thresholdMSPT - b.thresholdMSPT,
  );

/**
 * The server-wide population throttles configured for a server, sorted by
 * ascending player count. Which one is actually in force is resolved by the
 * API — read `ThrottleServerSummary.serverWideThrottle`.
 */
export const resolveEffectivePlayerCountThrottles = (
  configs: ChunkThrottleConfig[],
  fqdn: string | null | undefined,
  clusterId: string | null | undefined,
): PlayerCountThrottleConfig[] =>
  resolveEffectiveRules(configs.filter(isPlayerCountThrottleConfig), fqdn, clusterId).sort(
    (a, b) => a.minPlayerCount - b.minPlayerCount,
  );

/** True when `rule` is the population tier the API reports as currently in force. */
export const isThrottleInForce = (
  rule: PlayerCountThrottleConfig,
  active: ServerWideThrottle | null | undefined,
): boolean => active != null && active.id === rule.id && active.target === rule.target;

/**
 * The lowest MSPT at which a regular (non-force-loaded) chunk starts being
 * flagged under the given tiers — the reference point for "×N threshold"
 * annotations. `null` when no absolute threshold applies.
 */
export const getFlaggingFloorMspt = (configs: MsptThrottleConfig[]): number | null => {
  let floor: number | null = null;
  for (const config of configs) {
    if (config.foreloadedOnly || typeof config.thresholdMSPT !== 'number') continue;
    if (config.thresholdMSPT <= 0) continue;
    if (floor === null || config.thresholdMSPT < floor) floor = config.thresholdMSPT;
  }
  return floor;
};

/** Human phrase for a rule's throttle strength (tick-rate multiplier). */
export const describeThrottleMulti = (throttleMulti: number): string =>
  throttleMulti >= 1 ? 'warn only' : `${Math.round(throttleMulti * 100)}% tick rate`;

/** "x ago" for an epoch-milliseconds timestamp (all Throttle API timestamps are epoch ms). */
export const formatEpochMsAgo = (epochMs: number): string =>
  formatDistanceToNow(new Date(epochMs), { addSuffix: true });

/** Elapsed-time phrase without suffix ("7 minutes") — e.g. "flagged for 7 minutes". */
export const formatEpochMsDuration = (epochMs: number): string =>
  formatDistanceToNow(new Date(epochMs));

/** Approximate human duration of a game-tick count at 20 TPS (6000 → "≈ 5 min"). */
export const formatTicksApprox = (ticks: number): string => {
  const seconds = ticks / 20;
  if (seconds < 60) return `≈ ${Math.round(seconds)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) {
    const rounded = Math.round(minutes * 10) / 10;
    return `≈ ${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded} min`;
  }
  const hours = minutes / 60;
  const roundedHours = Math.round(hours * 10) / 10;
  return `≈ ${Number.isInteger(roundedHours) ? roundedHours.toFixed(0) : roundedHours} h`;
};
